import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MousePointerClick } from "lucide-react";
import { Reveal } from "./Reveal";

const LAYERS = [
  {
    id: "laminate",
    idx: "01",
    name: "Laminate",
    spec: "0.8 mm",
    h: "h-12",
    cls: "bg-gradient-to-b from-gold to-golddim text-royal",
    desc: "0.8 mm decorative laminate bonded to both faces of every panel — the surface you see and touch, chosen for a durable, easy-to-clean finish.",
  },
  {
    id: "adhesive",
    idx: "02",
    name: "Adhesive Layer",
    spec: "High-grade bonding",
    h: "h-7",
    cls: "bg-amber-300/80 text-royal",
    desc: "A high-grade adhesive bonds laminate and plywood under controlled pressure — the hidden layer that keeps every panel stable over years of use.",
  },
  {
    id: "plywood",
    idx: "03",
    name: "Plywood Panel",
    spec: "19 / 16 / 9 mm",
    h: "h-20",
    cls: "bg-wood text-ivory ply-texture",
    desc: "The structural core. Plywood in 19 mm, 16 mm and 9 mm thicknesses is selected according to each component and application, using 8 × 4 ft sheets for production.",
  },
  {
    id: "edgeband",
    idx: "04",
    name: "Edge Band",
    spec: "Machine-finished",
    h: "h-10",
    cls: "bg-woodlight text-royal",
    desc: "After cutting, exposed edges are processed through an edge-banding machine — protecting the board core and giving every component a clean, finished edge.",
  },
  {
    id: "screws",
    idx: "05",
    name: "Stainless-Steel Screws",
    spec: "High-grade",
    h: "h-14",
    cls: "bg-slate-300 text-royal",
    desc: "Depending on the component and application, structural assembly uses high-grade stainless-steel screws combined with adhesive bonding for secure, durable carcass assemblies.",
  },
  {
    id: "carcass",
    idx: "06",
    name: "Internal Carcass",
    spec: "Engineered box",
    h: "h-24",
    cls: "border-2 border-dashed border-ivory/40 bg-royaldeep text-ivory",
    desc: "Carefully aligned panels form the structural carcass — the engineered box behind your kitchen. It is the strength you never see, but always rely on.",
  },
  {
    id: "wpc",
    idx: "07",
    name: "WPC Protection",
    spec: "Base units",
    h: "h-12",
    cls: "bg-slate-600 text-ivory",
    desc: "In vulnerable areas such as base units, a high-grade WPC sheet is incorporated to help protect the structure from accidental water seepage from the stone countertop.",
  },
];

export const ExploreCarcass = () => {
  const [sel, setSel] = useState(2);
  const layer = LAYERS[sel];

  return (
    <section data-testid="explore-carcass" className="relative overflow-hidden bg-royal py-24 text-ivory md:py-32">
      <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-6">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-gold">Signature Engineering</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-tight md:text-5xl">
            Explore the <span className="serif-accent text-gold">Carcass.</span>
          </h2>
          <p className="mt-4 text-ivory/65">
            An engineered kitchen is a stack of decisions you cannot see. Select a layer to
            understand what it does — and why it matters.
          </p>
        </Reveal>

        <div className="mt-14 grid items-start gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <Reveal>
            <div className="[perspective:1100px]">
              <div className="space-y-2 [transform:rotateX(8deg)_rotateY(-4deg)]">
                {LAYERS.map((l, i) => (
                  <button
                    key={l.id}
                    data-testid={`carcass-layer-${l.id}`}
                    onClick={() => setSel(i)}
                    style={{ animationDelay: `${i * 0.35}s` }}
                    className={`flex w-full items-center justify-between gap-4 rounded-xl border px-5 py-3 text-left transition-all duration-300 ${
                      l.cls
                    } ${sel === i ? "translate-x-3 border-gold ring-2 ring-gold shadow-[0_18px_40px_rgba(0,0,0,0.35)]" : "border-white/10 hover:translate-x-1.5 hover:border-gold/50"}`}
                  >
                    <span className="flex items-center gap-4">
                      <span className="text-[10px] font-bold tracking-[0.2em] opacity-60">{l.idx}</span>
                      <span className="font-display text-sm font-medium md:text-base">{l.name}</span>
                    </span>
                    <span className="rounded-full bg-black/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]">
                      {l.spec}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-6 flex items-center gap-2 text-xs text-ivory/45">
              <MousePointerClick className="h-4 w-4 text-gold" /> Select any layer to inspect it
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              data-testid={`carcass-detail-${layer.id}`}
              className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur md:p-10"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={layer.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35 }}
                >
                  <p className="eyebrow text-gold">Layer {layer.idx}</p>
                  <h3 className="mt-4 font-display text-3xl font-medium md:text-4xl">{layer.name}</h3>
                  <span className="mt-4 inline-block rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-gold">
                    {layer.spec}
                  </span>
                  <p className="mt-6 leading-relaxed text-ivory/70">{layer.desc}</p>
                  <p className="mt-8 border-t border-white/10 pt-5 text-xs uppercase tracking-[0.2em] text-ivory/40">
                    Part of every Paramount carcass
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
